import React, { useEffect, useState } from 'react';
import { Shield, ChevronDown, ChevronUp, Search, Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const RATING_COLORS = {
  "Strong Buy": "text-green-400 bg-green-500/15 border-green-500/30",
  "Buy": "text-emerald-400 bg-emerald-500/15 border-emerald-500/30",
  "Hold": "text-yellow-400 bg-yellow-500/15 border-yellow-500/30",
  "Avoid": "text-red-400 bg-red-500/15 border-red-500/30",
  "Insufficient Data": "text-gray-400 bg-gray-500/15 border-gray-500/30",
};

const SCORE_COLORS = {
  2: "text-green-400",
  1: "text-yellow-400",
  0: "text-red-400",
  "-1": "text-red-400",
};

const SCORE_ICONS = {
  2: "bg-green-500/20",
  1: "bg-yellow-500/20",
  0: "bg-red-500/20",
  "-1": "bg-red-500/20",
};

const METRIC_LABELS = {
  pe_ratio: { name: "P/E Ratio", category: "Valuation" },
  price_to_book: { name: "Price-to-Book", category: "Valuation" },
  current_ratio: { name: "Current Ratio", category: "Financial Health" },
  quick_ratio: { name: "Quick Ratio", category: "Financial Health" },
  debt_to_equity: { name: "Debt/Equity", category: "Financial Health" },
  free_cash_flow: { name: "Free Cash Flow", category: "Cash Flow" },
  cash_flow_quality: { name: "Cash Flow Quality", category: "Cash Flow" },
  revenue_growth: { name: "Revenue Growth", category: "Growth" },
  roe: { name: "Return on Equity", category: "Profitability" },
  profit_margin: { name: "Net Profit Margin", category: "Profitability" },
};

function formatValue(key, metrics) {
  const val = metrics[key];
  if (val === null || val === undefined) return "N/A";
  switch (key) {
    case "pe_ratio": return val.toFixed(1);
    case "price_to_book": return val.toFixed(2);
    case "current_ratio": return val.toFixed(2);
    case "quick_ratio": return val.toFixed(2);
    case "debt_to_equity": return (val / 100).toFixed(2);
    case "free_cash_flow": return `$${(val / 1e9).toFixed(2)}B`;
    case "revenue_growth": return `${(val * 100).toFixed(1)}%`;
    case "roe": return `${(val * 100).toFixed(1)}%`;
    case "profit_margin": return `${(val * 100).toFixed(1)}%`;
    default: return String(val);
  }
}

function formatMarketCap(mc) {
  if (!mc) return "N/A";
  if (mc >= 1e12) return `$${(mc / 1e12).toFixed(1)}T`;
  if (mc >= 1e9) return `$${(mc / 1e9).toFixed(1)}B`;
  if (mc >= 1e6) return `$${(mc / 1e6).toFixed(0)}M`;
  return `$${mc.toLocaleString()}`;
}

export default function FinancialStrengthPage() {
  const { user } = useAuth();
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [search, setSearch] = useState('');
  const [ratingFilter, setRatingFilter] = useState('All');
  const [sectorFilter, setSectorFilter] = useState('All');
  const [sortBy, setSortBy] = useState('total_score');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedStock, setExpandedStock] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/financial-strength/latest`);
      const data = await res.json();
      if (data.success) {
        setStocks(data.data);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const triggerUpdate = async () => {
    setUpdating(true);
    try {
      const res = await authFetch(`${API_URL}/api/financial-strength/update`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        alert(data.message);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setUpdating(false);
    }
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  const sectors = ['All', ...new Set(stocks.map(s => s.sector).filter(Boolean).sort())];
  const ratings = ['All', 'Strong Buy', 'Buy', 'Hold', 'Avoid'];

  const filtered = stocks
    .filter(s => {
      if (search && !s.symbol.toLowerCase().includes(search.toLowerCase()) && !s.name?.toLowerCase().includes(search.toLowerCase())) return false;
      if (ratingFilter !== 'All' && s.rating !== ratingFilter) return false;
      if (sectorFilter !== 'All' && s.sector !== sectorFilter) return false;
      return true;
    })
    .sort((a, b) => {
      let aVal = a[sortBy] ?? -999;
      let bVal = b[sortBy] ?? -999;
      if (sortBy === 'symbol' || sortBy === 'name') {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="financial-strength-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <Shield className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Financial Strength</h1>
            <p className="text-sm text-muted-foreground">{stocks.length} stocks analyzed</p>
          </div>
        </div>
        {user?.role === 'admin' && (
          <button
            onClick={triggerUpdate}
            disabled={updating}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors text-sm"
            data-testid="update-analysis-btn"
          >
            <RefreshCw className={`w-4 h-4 ${updating ? 'animate-spin' : ''}`} />
            {updating ? 'Analyzing...' : 'Update Analysis'}
          </button>
        )}
      </div>

      {stocks.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">No Financial Data Yet</h2>
          <p className="text-muted-foreground text-sm mb-4">
            {user?.role === 'admin'
              ? 'Click "Update Analysis" to fetch financial data for all ASX stocks. This takes ~15 minutes.'
              : 'Financial analysis hasn\'t been run yet. Please check back later.'}
          </p>
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search stock..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:border-primary"
                data-testid="search-input"
              />
            </div>
            <select
              value={ratingFilter}
              onChange={(e) => setRatingFilter(e.target.value)}
              className="px-3 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none"
              data-testid="rating-filter"
            >
              {ratings.map(r => <option key={r} value={r}>{r === 'All' ? 'All Ratings' : r}</option>)}
            </select>
            <select
              value={sectorFilter}
              onChange={(e) => setSectorFilter(e.target.value)}
              className="px-3 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none"
              data-testid="sector-filter"
            >
              {sectors.map(s => <option key={s} value={s}>{s === 'All' ? 'All Sectors' : s}</option>)}
            </select>
          </div>

          {/* Results count */}
          <p className="text-xs text-muted-foreground mb-3">{filtered.length} stocks</p>

          {/* Table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="text-left p-3 cursor-pointer hover:text-foreground" onClick={() => toggleSort('symbol')}>
                      Symbol {sortBy === 'symbol' && (sortDir === 'asc' ? <ChevronUp className="inline w-3 h-3" /> : <ChevronDown className="inline w-3 h-3" />)}
                    </th>
                    <th className="text-left p-3 hidden md:table-cell">Sector</th>
                    <th className="text-right p-3 cursor-pointer hover:text-foreground" onClick={() => toggleSort('price')}>
                      Price {sortBy === 'price' && (sortDir === 'asc' ? <ChevronUp className="inline w-3 h-3" /> : <ChevronDown className="inline w-3 h-3" />)}
                    </th>
                    <th className="text-right p-3 cursor-pointer hover:text-foreground" onClick={() => toggleSort('total_score')}>
                      Score {sortBy === 'total_score' && (sortDir === 'asc' ? <ChevronUp className="inline w-3 h-3" /> : <ChevronDown className="inline w-3 h-3" />)}
                    </th>
                    <th className="text-center p-3">Rating</th>
                    <th className="w-8 p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((stock) => (
                    <React.Fragment key={stock.symbol}>
                      <tr
                        className="border-b border-border/50 hover:bg-card-hover cursor-pointer transition-colors"
                        onClick={() => setExpandedStock(expandedStock === stock.symbol ? null : stock.symbol)}
                        data-testid={`stock-row-${stock.symbol}`}
                      >
                        <td className="p-3">
                          <div className="font-semibold text-sm">{stock.symbol}</div>
                          <div className="text-xs text-muted-foreground truncate max-w-[150px]">{stock.name}</div>
                        </td>
                        <td className="p-3 text-xs text-muted-foreground hidden md:table-cell">{stock.sector}</td>
                        <td className="p-3 text-right text-sm">${stock.price?.toFixed(2)}</td>
                        <td className="p-3 text-right">
                          <span className="font-bold text-sm">{stock.total_score}</span>
                          <span className="text-xs text-muted-foreground">/{stock.max_score}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold border ${RATING_COLORS[stock.rating] || ''}`}>
                            {stock.rating}
                          </span>
                        </td>
                        <td className="p-3">
                          {expandedStock === stock.symbol ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </td>
                      </tr>

                      {/* Expanded Detail */}
                      {expandedStock === stock.symbol && (
                        <tr>
                          <td colSpan="6" className="p-0">
                            <div className="bg-background/50 p-4 border-b border-border" data-testid={`detail-${stock.symbol}`}>
                              {/* Insight */}
                              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mb-4">
                                <p className="text-sm text-blue-300">{stock.insight}</p>
                              </div>

                              {/* Red Flags */}
                              {stock.red_flags && stock.red_flags.length > 0 && (
                                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4" data-testid={`red-flags-${stock.symbol}`}>
                                  <div className="flex items-center gap-2 mb-2">
                                    <AlertTriangle className="w-4 h-4 text-red-400" />
                                    <span className="text-xs font-semibold text-red-400">Risk Flags</span>
                                  </div>
                                  <ul className="space-y-1">
                                    {stock.red_flags.map((flag, i) => (
                                      <li key={i} className="text-xs text-red-300 flex items-start gap-2">
                                        <span className="text-red-500 mt-0.5">*</span>
                                        <span>{flag}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Info row */}
                              <div className="flex flex-wrap gap-4 mb-4 text-xs text-muted-foreground">
                                <span>Market Cap: <strong className="text-foreground">{formatMarketCap(stock.market_cap)}</strong></span>
                                <span>Sector: <strong className="text-foreground">{stock.sector}</strong></span>
                                <span>Industry: <strong className="text-foreground">{stock.industry}</strong></span>
                                <span>Metrics Used: <strong className="text-foreground">{stock.metrics_counted}/10</strong></span>
                              </div>

                              {/* Score breakdown */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {Object.entries(stock.scores || {}).map(([key, scoreData]) => {
                                  const meta = METRIC_LABELS[key] || { name: key, category: "" };
                                  const scoreNum = scoreData?.score;
                                  const colorClass = scoreNum !== null ? (SCORE_COLORS[String(scoreNum)] || 'text-gray-400') : 'text-gray-500';
                                  const bgClass = scoreNum !== null ? (SCORE_ICONS[String(scoreNum)] || 'bg-gray-500/20') : 'bg-gray-500/10';
                                  const metricKey = key === "cash_flow_quality" ? null : key;
                                  const rawValue = metricKey ? formatValue(metricKey, stock.metrics) : (
                                    stock.metrics.operating_cash_flow && stock.metrics.net_income
                                      ? `OCF ${stock.metrics.operating_cash_flow > stock.metrics.net_income ? '>' : '<'} NI`
                                      : "N/A"
                                  );

                                  return (
                                    <div key={key} className="flex items-center justify-between p-2 rounded-lg bg-card border border-border/50">
                                      <div className="flex items-center gap-2">
                                        <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${bgClass} ${colorClass}`}>
                                          {scoreNum !== null ? scoreNum : '-'}
                                        </div>
                                        <div>
                                          <span className="text-xs font-medium">{meta.name}</span>
                                          <span className="text-[10px] text-muted-foreground ml-1">({meta.category})</span>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <span className="text-xs font-mono">{rawValue}</span>
                                        <span className={`text-[10px] ml-1 ${colorClass}`}>{scoreData?.label}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
